import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Skeleton } from '../components/ui/skeleton';
import { useCurrency } from '../context/CurrencyContext';
import { api } from '../services/api';
import {
  ShieldAlert, ShieldCheck, AlertTriangle, Calendar, TrendingUp,
  PieChart, Sparkles, ArrowRight, ChevronRight, Settings as SettingsIcon, Info,
  Wrench,
} from 'lucide-react';
import { getCategoryColor } from '../utils/helpers';
import { toast } from 'sonner';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import FixThisModal from '../components/FixThisModal';

const InfoHint = ({ text, testid }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        data-testid={testid}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-muted-foreground hover:text-gold transition-colors align-middle ml-1"
        aria-label="What does this mean?"
        onClick={(e) => e.preventDefault()}
      >
        <Info size={11} />
      </button>
    </TooltipTrigger>
    <TooltipContent
      side="top"
      sideOffset={6}
      className="max-w-[260px] bg-[#0F0F0F] border border-gold/30 text-zinc-200 text-[11px] leading-relaxed px-3 py-2 normal-case tracking-normal"
    >
      {text}
    </TooltipContent>
  </Tooltip>
);

const HealthDonut = ({ total, grade, breakdown }) => {
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, total)) / 100) * circumference;
  const tone = total >= 80 ? '#10b981' : total >= 60 ? '#d4af37' : total >= 40 ? '#f59e0b' : '#ef4444';

  return (
    <div className="luxury-card p-6 mb-6 bg-gradient-to-br from-gold/5 via-transparent to-transparent border-gold/30" data-testid="health-score-card">
      <div className="flex items-start gap-6 flex-wrap">
        <div className="relative flex-shrink-0">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r={radius} stroke="#1a1a1a" strokeWidth="10" fill="none" />
            <circle
              cx="70" cy="70" r={radius}
              stroke={tone} strokeWidth="10" fill="none"
              strokeDasharray={circumference} strokeDashoffset={offset}
              strokeLinecap="round"
              transform="rotate(-90 70 70)"
              style={{ transition: 'stroke-dashoffset 0.7s ease' }}
            />
            <text x="70" y="68" textAnchor="middle" fontFamily="serif" fontSize="32" fill="#fff">{total}</text>
            <text x="70" y="88" textAnchor="middle" fontSize="10" fill="#888" letterSpacing="2">/ 100</text>
          </svg>
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-[10px] uppercase tracking-wider text-gold mb-1">Portfolio Health Score</p>
          <p className="font-serif text-2xl text-white mb-1">{grade}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            A weighted score blending coverage, diversification, exposure and upcoming deadlines.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {Object.entries(breakdown).map(([key, b]) => {
              const pct = b.max ? (b.score / b.max) * 100 : 0;
              return (
                <div key={key} className="bg-[#0F0F0F] border border-[#1A1A1A] rounded p-2" data-testid={`hs-${key}`}>
                  <div className="flex justify-between items-center text-[10px]">
                    <span className="text-muted-foreground uppercase tracking-wider truncate">{b.label}</span>
                    <span className="text-white font-medium ml-2">{b.score}/{b.max}</span>
                  </div>
                  <div className="h-1 bg-[#1A1A1A] rounded-full overflow-hidden mt-1">
                    <div className="h-full" style={{ width: `${pct}%`, background: tone, transition: 'width 0.6s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ScoreBlock = ({ value, total, label, tone = 'gold', suffix = '', hint, testid }) => {
  const pct = total ? Math.min(100, Math.round((value / total) * 100)) : 0;
  const tones = {
    gold:    { fg: 'text-gold',          bg: 'from-gold/20',          ring: 'border-gold/30' },
    emerald: { fg: 'text-emerald-400',   bg: 'from-emerald-500/15',   ring: 'border-emerald-500/30' },
    amber:   { fg: 'text-amber-400',     bg: 'from-amber-500/15',     ring: 'border-amber-500/30' },
    red:     { fg: 'text-red-400',       bg: 'from-red-500/15',       ring: 'border-red-500/30' },
  };
  const t = tones[tone];
  return (
    <div className={`luxury-card p-5 bg-gradient-to-br ${t.bg} to-transparent border ${t.ring}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2 flex items-center">
        {label}
        {hint && <InfoHint text={hint} testid={testid} />}
      </p>
      <p className={`font-serif text-3xl ${t.fg}`}>{value}{suffix}</p>
      {total && <p className="text-[10px] text-muted-foreground mt-1">of {total}{suffix} possible</p>}
      <div className="mt-3 h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div className={`h-full ${tone === 'red' ? 'bg-red-400' : tone === 'amber' ? 'bg-amber-400' : tone === 'emerald' ? 'bg-emerald-400' : 'bg-gold'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, count, tone = 'gold', hint, testid }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon size={14} className={tone === 'red' ? 'text-red-400' : tone === 'amber' ? 'text-amber-400' : 'text-gold'} />
    <h3 className="text-sm uppercase tracking-wider text-white">{title}</h3>
    {hint && <InfoHint text={hint} testid={testid} />}
    {count !== undefined && (
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
        tone === 'red' ? 'bg-red-500/15 text-red-400' :
        tone === 'amber' ? 'bg-amber-500/15 text-amber-400' :
        'bg-gold/15 text-gold'
      }`}>{count}</span>
    )}
  </div>
);

const AssetRow = ({ a, formatCurrency, accent, rightContent, onFix }) => (
  <div
    className="flex items-center justify-between gap-3 p-3 bg-[#0F0F0F] border border-[#1A1A1A] hover:border-gold/30 rounded transition-colors"
    data-testid={`risk-asset-${a.asset_id}`}
  >
    <Link to={`/assets/${a.asset_id}`} className="flex items-center gap-3 min-w-0 flex-1">
      <div className={`w-1 h-9 rounded-full ${accent}`} />
      <div className="min-w-0">
        <p className="text-white text-sm font-medium truncate">{a.name}</p>
        <p className={`text-[10px] capitalize ${getCategoryColor(a.category)}`}>{a.category}</p>
      </div>
    </Link>
    <div className="text-right flex items-center gap-3 flex-shrink-0">
      {rightContent}
      {onFix && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onFix(a); }}
          data-testid={`fix-this-btn-${a.asset_id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-md bg-gradient-to-r from-gold to-amber-300 text-black shadow-[0_0_12px_rgba(212,175,55,0.35)] hover:shadow-[0_0_20px_rgba(212,175,55,0.55)] hover:scale-105 transition-all whitespace-nowrap ring-1 ring-gold/40 hover:ring-gold/60"
        >
          <Wrench size={11} className="stroke-[2.5]" />
          Fix this
        </button>
      )}
      <Link to={`/assets/${a.asset_id}`} className="text-muted-foreground hover:text-white">
        <ChevronRight size={14} />
      </Link>
    </div>
  </div>
);

export default function Risk() {
  const { formatCurrency } = useCurrency();
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixModal, setFixModal] = useState({ open: false, issue: null, asset: null });
  const openFix = (issue) => (asset) => setFixModal({ open: true, issue, asset });
  const closeFix = () => setFixModal({ open: false, issue: null, asset: null });
  const [searchParams] = useSearchParams();
  const focus = searchParams.get('focus'); // uninsured | underinsured | expiring | concentration

  useEffect(() => {
    api.getRiskSummary()
      .then(r => setRisk(r.data))
      .catch(() => setRisk(null))
      .finally(() => setLoading(false));
  }, []);

  // Once data has loaded, scroll the requested section into view and pulse-highlight it.
  useEffect(() => {
    if (loading || !focus) return;
    const sectionId = focus === 'expiring' ? 'section-expiring-30' : `section-${focus}`;
    const el = document.querySelector(`[data-testid="${sectionId}"]`);
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-gold/60', 'shadow-[0_0_30px_rgba(212,175,55,0.35)]');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-gold/60', 'shadow-[0_0_30px_rgba(212,175,55,0.35)]');
      }, 2800);
    }, 150);
  }, [loading, focus]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="risk-loading">
          <Skeleton className="h-3 w-32 mb-3 bg-gold/10" />
          <Skeleton className="h-9 w-64 mb-8 bg-white/5" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[0,1,2,3].map(i => <Skeleton key={i} className="h-32 bg-white/5" />)}
          </div>
          <Skeleton className="h-64 bg-white/5 mb-6" />
          <Skeleton className="h-64 bg-white/5" />
        </div>
      </DashboardLayout>
    );
  }

  if (!risk) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="risk-error">
          <p className="text-muted-foreground">Could not load risk data. Please refresh.</p>
        </div>
      </DashboardLayout>
    );
  }

  const s = risk.summary;

  // Coverage tone
  const coverageTone = s.coverage_ratio_pct >= 90 ? 'emerald' :
                       s.coverage_ratio_pct >= 70 ? 'gold' :
                       s.coverage_ratio_pct >= 40 ? 'amber' : 'red';

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="risk-page">
        <div className="mb-8 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-gold uppercase tracking-[0.2em] text-xs mb-2">Risk & Coverage</p>
            <h1 className="font-serif text-3xl text-white">What's at risk in your vault?</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
              Live analysis across coverage gaps, expiring policies, and concentration risk. Updated every time you change an asset's value or policy.
            </p>
          </div>
          <Link
            to="/action-queue"
            data-testid="risk-saved-actions-link"
            className="text-[11px] uppercase tracking-wider text-muted-foreground hover:text-gold border border-[#262626] hover:border-gold/30 px-3 py-1.5 rounded transition-colors inline-flex items-center gap-1.5 mt-2"
          >
            <Wrench size={11} /> View saved actions
          </Link>
        </div>

        {/* Real Portfolio Health Score */}
        {risk.health_score && (
          <HealthDonut
            total={risk.health_score.total}
            grade={risk.health_score.grade}
            breakdown={risk.health_score.breakdown}
          />
        )}

        {/* Concentration threshold control */}
        <div className="luxury-card p-4 mb-6 flex items-center gap-4 flex-wrap" data-testid="concentration-control">
          <SettingsIcon size={14} className="text-muted-foreground" />
          <div className="flex-1 min-w-[200px]">
            <p className="text-xs text-white flex items-center">
              Concentration alert threshold
              <InfoHint
                testid="hint-threshold"
                text="The percentage above which a single asset is flagged as 'too concentrated'. The industry default for high-net-worth portfolios is 25–30%. A lower number = stricter (more items flagged); a higher number = more relaxed."
              />
            </p>
            <p className="text-[10px] text-muted-foreground">Flag any single asset that exceeds this percentage of your portfolio.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min="10" max="80" step="5"
              value={Math.round(s.concentration_threshold_pct ?? 30)}
              onChange={async (e) => {
                const pct = Number(e.target.value);
                setRisk(prev => ({ ...prev, summary: { ...prev.summary, concentration_threshold_pct: pct } }));
              }}
              onMouseUp={async (e) => {
                const pct = Number(e.target.value);
                try {
                  await api.updateRiskPreferences({ concentration_threshold_pct: pct });
                  const r = await api.getRiskSummary();
                  setRisk(r.data);
                  toast.success(`Threshold set to ${pct}%`);
                } catch { toast.error('Could not save'); }
              }}
              onTouchEnd={async (e) => {
                const pct = Number(e.target.value);
                try {
                  await api.updateRiskPreferences({ concentration_threshold_pct: pct });
                  const r = await api.getRiskSummary();
                  setRisk(r.data);
                } catch {}
              }}
              className="w-40 accent-gold"
              data-testid="concentration-slider"
            />
            <span className="text-gold text-sm font-medium w-10 text-right" data-testid="concentration-value">{Math.round(s.concentration_threshold_pct ?? 30)}%</span>
          </div>
        </div>

        {/* Top stat row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="risk-summary-stats">
          <ScoreBlock
            label="Coverage Ratio" value={s.coverage_ratio_pct} total={100} suffix="%" tone={coverageTone}
            testid="hint-coverage-ratio"
            hint="The total amount of insurance you currently hold across the vault, expressed as a percentage of the total current value of every asset. 100% means every pound of value is covered. Below 70% indicates a meaningful protection gap."
          />
          <ScoreBlock
            label="Underinsured" value={s.underinsured_count} total={s.asset_count}
            tone={s.underinsured_count > 0 ? 'red' : 'emerald'}
            testid="hint-underinsured-stat"
            hint="Items that have a policy attached but where the insured value is more than 5% below the current market value. These are at risk of a partial-loss shortfall."
          />
          <ScoreBlock
            label="Expiring (30d)" value={s.expiring_30_count} total={s.asset_count}
            tone={s.expiring_30_count > 0 ? 'amber' : 'emerald'}
            testid="hint-expiring"
            hint="Policies that will lapse within the next 30 days. Renew or replace these urgently to avoid a coverage gap."
          />
          <ScoreBlock
            label="Concentration" value={s.concentration_count} total={s.asset_count}
            tone={s.concentration_count > 0 ? 'amber' : 'emerald'}
            testid="hint-concentration"
            hint={`Single assets that exceed ${Math.round(s.concentration_threshold_pct ?? 30)}% of your total portfolio value. High concentration means losing or damaging one item materially impacts your wealth - adjust the threshold above to set your own tolerance.`}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Underinsured assets */}
          <div className="luxury-card p-5" data-testid="section-underinsured">
            <SectionHeader
              icon={ShieldAlert} title="Underinsured" count={risk.underinsured.length}
              tone={risk.underinsured.length > 0 ? 'red' : 'emerald'}
              testid="hint-section-underinsured"
              hint="Underinsured = the asset has a policy attached but the insured value is below the current market value (5% tolerance). If the asset is destroyed today, you would receive less than its replacement cost. Items with no insurance at all appear in the section below - not here."
            />
            {risk.underinsured.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                {risk.uninsured.length > 0
                  ? `None of your insured assets are underinsured - but ${risk.uninsured.length} asset${risk.uninsured.length === 1 ? ' has' : 's have'} no cover at all (see below).`
                  : risk.expiring_30.length === 0 && (s.asset_count ?? 0) === 0
                    ? 'Add your first asset to see underinsurance analysis.'
                    : 'Every insured asset has cover at or above its current value. Well done.'}
              </p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  Total cover gap: <span className="text-red-400 font-semibold">{formatCurrency(s.underinsured_total_gap)}</span>
                </p>
                <div className="space-y-2">
                  {risk.underinsured.slice(0, 5).map(a => (
                    <AssetRow key={a.asset_id} a={a} formatCurrency={formatCurrency} accent="bg-red-400" onFix={openFix('underinsured')} rightContent={
                      <div>
                        <p className="text-red-400 text-xs font-semibold">+{formatCurrency(a.gap)}</p>
                        <p className="text-[10px] text-muted-foreground">{a.gap_pct}% under</p>
                      </div>
                    } />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Uninsured assets */}
          <div className="luxury-card p-5" data-testid="section-uninsured">
            <SectionHeader
              icon={AlertTriangle} title="No cover at all" count={risk.uninsured.length}
              tone={risk.uninsured.length > 0 ? 'amber' : 'emerald'}
              testid="hint-section-uninsured"
              hint="Assets in your vault that have no insurance policy attached at all. The full current value is fully exposed in the event of theft, damage or loss."
            />
            {risk.uninsured.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Every asset in your vault has insurance attached.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  Total exposed value: <span className="text-amber-400 font-semibold">{formatCurrency(s.uninsured_total)}</span>
                </p>
                <div className="space-y-2">
                  {risk.uninsured.slice(0, 5).map(a => (
                    <AssetRow key={a.asset_id} a={a} formatCurrency={formatCurrency} accent="bg-amber-400" onFix={openFix('uninsured')} rightContent={
                      <div>
                        <p className="text-amber-400 text-xs font-semibold">{formatCurrency(a.current_value)}</p>
                        <p className="text-[10px] text-muted-foreground">at risk</p>
                      </div>
                    } />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Expiring next 30 days */}
          <div className="luxury-card p-5" data-testid="section-expiring-30">
            <SectionHeader icon={Calendar} title="Expiring within 30 days" count={risk.expiring_30.length} tone={risk.expiring_30.length > 0 ? 'red' : 'emerald'} />
            {risk.expiring_30.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No urgent renewals in the next 30 days.</p>
            ) : (
              <div className="space-y-2">
                {risk.expiring_30.slice(0, 5).map(a => (
                  <AssetRow key={a.asset_id} a={a} formatCurrency={formatCurrency} accent="bg-red-400" onFix={openFix('expiring')} rightContent={
                    <div>
                      <p className="text-red-400 text-xs font-semibold">{a.days_left < 0 ? 'Expired' : `${a.days_left}d`}</p>
                      <p className="text-[10px] text-muted-foreground truncate max-w-[120px]">{a.provider || 'No provider'}</p>
                    </div>
                  } />
                ))}
              </div>
            )}
          </div>

          {/* Expiring next 60-90 days */}
          <div className="luxury-card p-5" data-testid="section-expiring-soon">
            <SectionHeader icon={Calendar} title="Renewals 30–90 days out" count={risk.expiring_60.length + risk.expiring_90.length} tone="amber" />
            {risk.expiring_60.length + risk.expiring_90.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No upcoming renewals in this window.</p>
            ) : (
              <div className="space-y-2">
                {[...risk.expiring_60, ...risk.expiring_90].slice(0, 5).map(a => (
                  <AssetRow key={a.asset_id} a={a} formatCurrency={formatCurrency} accent="bg-amber-400" onFix={openFix('expiring')} rightContent={
                    <div>
                      <p className="text-amber-400 text-xs font-semibold">{a.days_left}d</p>
                      <p className="text-[10px] text-muted-foreground truncate max-w-[120px]">{a.provider || 'No provider'}</p>
                    </div>
                  } />
                ))}
              </div>
            )}
          </div>

          {/* Concentration risk */}
          <div className="luxury-card p-5" data-testid="section-concentration">
            <SectionHeader
              icon={TrendingUp} title="Concentration risk" count={risk.concentration.length}
              tone={risk.concentration.length > 0 ? 'amber' : 'emerald'}
              testid="hint-section-concentration"
              hint={`Any single asset that exceeds ${Math.round(s.concentration_threshold_pct ?? 30)}% of total portfolio value. Adjust the slider at the top of the page to set a stricter or looser threshold.`}
            />
            {risk.concentration.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No single asset exceeds 30% of your portfolio. Well-diversified.</p>
            ) : (
              <div className="space-y-2">
                {risk.concentration.map(a => (
                  <AssetRow key={a.asset_id} a={a} formatCurrency={formatCurrency} accent="bg-amber-400" onFix={openFix('concentration')} rightContent={
                    <div>
                      <p className="text-amber-400 text-xs font-semibold">{a.pct_of_portfolio}%</p>
                      <p className="text-[10px] text-muted-foreground">of portfolio</p>
                    </div>
                  } />
                ))}
              </div>
            )}
          </div>

          {/* Diversification */}
          <div className="luxury-card p-5" data-testid="section-diversification">
            <SectionHeader
              icon={PieChart} title="Category diversification"
              testid="hint-section-diversification"
              hint="A breakdown of your portfolio by asset category (cars, art, watches, etc.). A well-diversified portfolio doesn't have any one category dominating - typically you want no single category to exceed 50–60% of total value."
            />
            {risk.diversification.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No assets recorded yet.</p>
            ) : (
              <div className="space-y-2">
                {risk.diversification.map((d, i) => (
                  <div key={i} className="space-y-1" data-testid={`div-${d.category}`}>
                    <div className="flex justify-between text-xs">
                      <span className={`capitalize ${getCategoryColor(d.category)}`}>{d.category}</span>
                      <span className="text-muted-foreground">{formatCurrency(d.value)} · {d.pct}%</span>
                    </div>
                    <div className="h-1 bg-[#1A1A1A] rounded-full overflow-hidden">
                      <div className={`h-full ${getCategoryColor(d.category).replace('text-', 'bg-')}`} style={{ width: `${d.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom hint */}
        <div className="mt-8 luxury-card p-4 flex items-center gap-3 border-gold/20" data-testid="risk-ai-hint">
          <ShieldCheck size={16} className="text-gold flex-shrink-0" />
          <p className="text-xs text-muted-foreground flex-1">
            Want a tailored action plan? Ask AI Insights to draft outreach to your insurer about underinsured items, or to summarise your renewal calendar.
          </p>
          <Link to="/insights">
            <span className="inline-flex items-center gap-1 text-xs text-gold hover:underline whitespace-nowrap">
              Open AI Insights <ArrowRight size={12} />
            </span>
          </Link>
        </div>
      </div>

      <FixThisModal
        open={fixModal.open}
        onClose={closeFix}
        issue={fixModal.issue}
        asset={fixModal.asset}
        formatCurrency={formatCurrency}
      />
    </DashboardLayout>
  );
}
